/**
 * Экран статьи: хлебные крошки, заголовок, время чтения и дата обновления, сводка сбоку,
 * оформленный текст (enhance.js), оглавление «На этой странице» с подсветкой текущего
 * раздела и прогрессом чтения, соседние статьи темы и сообщение об ошибке редакторам.
 */
import { api, apiError, btn, el, fmtDate, icon, onLeave, openDialog, play, SPRING, toast } from "../../js/ui.js";
import { articlePath, readingMinutes, relativeDate } from "./content.js";
import { can, fetchArticle, neighbours, rememberArticle, SECTIONS, topicById, wiki } from "./data.js";
import { enhanceArticle } from "./enhance.js";
import { errorScreen, notFoundScreen } from "./pages.js";
import { loginUrl } from "./shell.js";

/** Отступ от верха окна до заголовка раздела при прокрутке: шапка 64px + воздух. */
const HEADING_OFFSET = 110;

/**
 * Собрать экран статьи. Возвращает описание экрана для app.js:
 * { node, title, reader: { section, slug, openToc }, onShown }.
 */
export async function renderArticle(slug) {
  const response = await fetchArticle(slug);
  if (response.status === 404) return notFoundScreen(slug);
  if (!response.ok) return errorScreen(response);
  const article = response.data;
  rememberArticle(slug, article.title);

  const prose = el("div", { class: "wiki-prose", html: article.renderedHtml || "" });
  // Первый заголовок текста повторяет название статьи — его показывает шапка статьи.
  if (prose.firstElementChild && prose.firstElementChild.tagName === "H1") prose.firstElementChild.remove();
  const { headings } = enhanceArticle(prose);
  const toc = article.toc_enabled && headings.length >= 2 ? createToc(headings) : null;

  const infobox = article.infoboxHtml
    ? el("aside", { class: "wiki-infobox", "aria-label": "Сводка", html: article.infoboxHtml })
    : null;
  if (infobox) enhanceArticle(infobox, { interactive: false });

  const node = el("div", { class: "wiki-page" }, [
    el("article", { class: "wiki-article" }, [
      articleHeader(article, readingMinutes(prose.textContent)),
      infobox,
      prose,
      articleFooter(article),
    ]),
    toc ? el("aside", { class: "wiki-page__toc" }, toc.node) : null,
  ]);

  return {
    node,
    title: article.title,
    reader: { slug, openToc: toc ? () => openTocSheet(headings) : null },
    onShown: () => { if (toc) toc.start(prose); },
  };
}

/** Хлебные крошки, заголовок и строка сведений (время чтения, обновление, правка). */
function articleHeader(article, minutes) {
  const topic = topicById(article.topic_id);
  const separator = () => el("span", { class: "wiki-crumbs__sep", "aria-hidden": "true" }, icon("chevron-right", 14));
  return el("header", { class: "wiki-article__header" }, [
    el("nav", { class: "wiki-crumbs", "aria-label": "Раздел" }, [
      el("a", { href: "/wiki/" }, "Вики"),
      article.section === "dev" ? [separator(), el("a", { href: "/wiki/dev" }, SECTIONS.dev.label)] : null,
      topic ? [separator(), el("span", null, topic.name)] : null,
    ]),
    el("h1", { class: "wiki-title" }, article.title),
    el("div", { class: "wiki-meta" }, [
      el("span", null, [icon("clock", 15), `${minutes} мин чтения`]),
      article.updated_at ? el("span", { title: fmtDate(article.updated_at) }, [icon("history", 15), `Обновлено ${relativeDate(article.updated_at)}`]) : null,
      article.status === "DRAFT" ? el("span", { class: "wiki-badge" }, "Черновик — видят только редакторы") : null,
      can.edit ? el("a", { href: `/wiki/edit/${article.slug}`, class: "jk-btn jk-btn--ghost jk-btn--sm wiki-meta__edit" }, [icon("pencil", 15), "Править"]) : null,
    ]),
  ]);
}

/** Низ статьи: соседние статьи темы, подпись последней правки, сообщение об ошибке. */
function articleFooter(article) {
  const { previous, next } = neighbours(article.slug);
  const pagerLink = (entry, direction) => el("a", { href: articlePath(entry.slug), class: `wiki-pager__link wiki-pager__link--${direction}` }, [
    el("span", { class: "wiki-pager__hint" }, direction === "previous"
      ? [icon("arrow-left", 15), "Предыдущая"]
      : ["Следующая", icon("arrow-right", 15)]),
    el("span", { class: "wiki-pager__title" }, entry.title),
  ]);
  const report = wiki.session.authenticated
    ? btn("Сообщить", () => openReportDialog(article), "default", { iconName: "flag", size: "sm" })
    : el("a", { href: loginUrl(), class: "jk-btn jk-btn--default jk-btn--sm" }, "Войти, чтобы сообщить");
  return el("footer", { class: "wiki-article__footer" }, [
    previous || next ? el("nav", { class: "wiki-pager", "aria-label": "Соседние статьи" }, [
      previous ? pagerLink(previous, "previous") : el("span"),
      next ? pagerLink(next, "next") : el("span"),
    ]) : null,
    el("div", { class: "wiki-feedback" }, [
      el("div", null, [
        el("p", { class: "font-semibold" }, "Нашли ошибку или устаревшие сведения?"),
        el("p", { class: "text-sm text-muted mt-1" }, "Напишите редакторам — ответ придёт в уведомления на сайте."),
      ]),
      report,
    ]),
    article.show_signature && article.author_username
      ? el("p", { class: "wiki-signature" }, `Последняя правка: ${article.author_username}`)
      : null,
  ]);
}

/** Окно «Сообщить об ошибке» — уходит редакторам во вкладку «Репорты» управления. */
function openReportDialog(article) {
  const text = el("textarea", {
    class: "jk-input wiki-textarea",
    rows: "5",
    maxlength: "4000",
    placeholder: "Что не так: устаревшая команда, неверные цифры, непонятное место…",
    "aria-label": "Описание ошибки",
  });
  let dialog = null;
  const send = btn("Отправить", async () => {
    const message = text.value.trim();
    if (message.length < 5) {
      toast("Опишите ошибку чуть подробнее", "error");
      text.focus();
      return;
    }
    const response = await api.post("/api/bug-reports", {
      title: `Ошибка в статье «${article.title}»`,
      description: message,
      article_slug: article.slug,
    });
    if (!response.ok) {
      apiError("Сообщение не отправлено", response);
      return;
    }
    dialog.close();
    toast("Спасибо! Редакторы посмотрят и ответят в уведомлениях");
  }, "primary");
  dialog = openDialog({
    title: "Сообщить об ошибке",
    body: el("div", null, [el("p", { class: "text-sm text-muted mb-4" }, `Статья «${article.title}»`), text]),
    actions: [btn("Отмена", () => dialog.close()), send],
  });
  text.focus();
}

// ──────────────────────────── Оглавление ────────────────────────────

/**
 * Оглавление «На этой странице» для широкого экрана: список разделов, метка текущего
 * раздела (переезжает пружиной) и полоса прочитанного вдоль списка.
 * start(prose) — начать следить за прокруткой, когда статья уже на странице.
 */
function createToc(headings) {
  const links = new Map();
  for (const heading of headings) {
    links.set(heading.id, el("a", {
      href: `#${heading.id}`,
      class: `wiki-toc__link${heading.level === 3 ? " wiki-toc__link--sub" : ""}`,
    }, heading.text));
  }
  const fill = el("i");
  const marker = el("span", { class: "wiki-toc__marker", "aria-hidden": "true" });
  const node = el("nav", { class: "wiki-toc", "aria-label": "На этой странице" }, [
    el("p", { class: "wiki-toc__title" }, "На этой странице"),
    el("div", { class: "wiki-toc__body" }, [
      el("span", { class: "wiki-toc__rail", "aria-hidden": "true" }, fill),
      marker,
      el("div", { class: "wiki-toc__list" }, [...links.values()]),
    ]),
  ]);
  let activeId = null;
  let markerTop = null;

  function setActive(id) {
    if (id === activeId) return;
    activeId = id;
    for (const [linkId, link] of links) {
      if (linkId === id) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    }
    const link = links.get(id);
    if (!link || !link.offsetParent) return;
    const top = link.offsetTop;
    marker.style.height = `${link.offsetHeight}px`;
    marker.style.transform = `translateY(${top}px)`;
    marker.style.opacity = "1";
    if (markerTop !== null) play(marker, { transform: [`translateY(${markerTop}px)`, `translateY(${top}px)`] }, SPRING);
    markerTop = top;
  }

  function start(prose) {
    let frame = 0;
    const update = () => {
      frame = 0;
      let current = headings[0];
      for (const heading of headings) {
        if (heading.node.getBoundingClientRect().top - HEADING_OFFSET <= 1) current = heading;
        else break;
      }
      setActive(current.id);
      const box = prose.getBoundingClientRect();
      const readable = Math.max(1, box.height - window.innerHeight * 0.5);
      const passed = Math.min(1, Math.max(0, (HEADING_OFFSET - box.top) / readable));
      fill.style.transform = `scaleY(${passed})`;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    update();
    onLeave(node, () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame);
    });
  }

  return { node, start };
}

/** Оглавление нижним листом на телефоне: после выбора раздела лист закрывается. */
function openTocSheet(headings) {
  const list = el("nav", { class: "wiki-toc wiki-toc--sheet", "aria-label": "На этой странице" }, [
    el("p", { class: "wiki-toc__title" }, "На этой странице"),
    el("div", { class: "wiki-toc__list" }, headings.map((heading) => el("a", {
      href: `#${heading.id}`,
      class: `wiki-toc__link${heading.level === 3 ? " wiki-toc__link--sub" : ""}`,
    }, heading.text))),
  ]);
  const sheet = openDialog({ body: list, sheet: true, label: "На этой странице" });
  list.addEventListener("click", (event) => {
    if (event.target.closest("a[href]")) sheet.close();
  });
}
